import { useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2, Pencil, RotateCcw } from 'lucide-react';
import { useStore } from '../../../store';
import type {
  FaxRouteSettingRow,
  MiscTaskTypeId,
} from '../../../services/faxRouteSettingsApi';
import { useFaxRouteSettings } from './useFaxRouteSettings';

/**
 * Admin, Fax, Document routing: where each fax type is filed in the chart.
 *
 * A super user can open Admin at an agency without eFax, where the routes
 * refuse, so the table and its request are only mounted when eFax is on.
 */
export default function FaxDocumentRoutingSection() {
  const hasFax = useStore(s => s.hasProduct('fax'));
  return (
    <div className='space-y-5'>
      <div>
        <h2 className='text-xl font-semibold'>Document routing</h2>
        <p className='text-sm text-muted-foreground'>
          Choose where each fax type is filed. Staff can still pick another task
          when filing.
        </p>
      </div>
      {hasFax ? (
        <RoutingTable />
      ) : (
        <p className='text-sm'>eFax is not on for this agency.</p>
      )}
    </div>
  );
}

function RoutingTable() {
  const { rows, miscTaskTypes, loading, failed, reload, save, reset } =
    useFaxRouteSettings();
  const [editingId, setEditingId] = useState<string | null>(null);
  // Per row, so one row finishing never re-enables another still in flight.
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());

  if (loading) {
    return (
      <Loader2
        className='h-5 w-5 animate-spin'
        aria-label='Loading document routing'
      />
    );
  }

  if (failed) {
    return (
      <div className='flex flex-wrap items-center gap-3'>
        <p className='text-sm'>Could not load the routing.</p>
        <Button variant='outline' size='sm' onClick={reload}>
          Retry
        </Button>
      </div>
    );
  }

  const run = async (faxTypeId: string, action: () => Promise<boolean>) => {
    setBusyIds(prev => new Set(prev).add(faxTypeId));
    const ok = await action();
    setBusyIds(prev => {
      const next = new Set(prev);
      next.delete(faxTypeId);
      return next;
    });
    // Only close the editor if it is still this row's.
    if (ok) setEditingId(current => (current === faxTypeId ? null : current));
  };

  return (
    <div className='overflow-x-auto rounded-md border'>
      <Table className='min-w-[560px]'>
        <TableHeader>
          <TableRow>
            <TableHead>Fax type</TableHead>
            <TableHead>Files to</TableHead>
            <TableHead className='w-px' />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <RoutingRow
              key={row.fax_type_id}
              row={row}
              miscTaskTypes={miscTaskTypes}
              editing={editingId === row.fax_type_id}
              busy={busyIds.has(row.fax_type_id)}
              onEdit={() => setEditingId(row.fax_type_id)}
              onCancel={() => setEditingId(null)}
              onSave={(miscTaskType, taskName) =>
                run(row.fax_type_id, () =>
                  save(row.fax_type_id, {
                    misc_task_type: miscTaskType,
                    task_name: taskName.trim() || null,
                  })
                )
              }
              onReset={() => run(row.fax_type_id, () => reset(row.fax_type_id))}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface RoutingRowProps {
  row: FaxRouteSettingRow;
  miscTaskTypes: MiscTaskTypeId[];
  editing: boolean;
  busy: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (miscTaskType: MiscTaskTypeId, taskName: string) => void;
  onReset: () => void;
}

/** The offered task type equal to a stored value, or undefined if not offered. */
function offeredType(
  miscTaskTypes: MiscTaskTypeId[],
  value: string
): MiscTaskTypeId | undefined {
  return miscTaskTypes.find(type => type === value);
}

function RoutingRow({
  row,
  miscTaskTypes,
  editing,
  busy,
  onEdit,
  onCancel,
  onSave,
  onReset,
}: RoutingRowProps) {
  const overridden = row.override != null;
  // A task type this build does not offer is ignored by routing, so the row
  // shows Roger's default and says the saved choice is unavailable.
  const unavailable =
    row.override != null &&
    offeredType(miscTaskTypes, row.override.misc_task_type) === undefined;
  return (
    <>
      <TableRow>
        <TableCell className='font-medium'>
          <div className='flex flex-wrap items-center gap-2'>
            {row.fax_type_name}
            {row.retired && (
              <Badge variant='outline' className='whitespace-nowrap'>
                Retired
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          {overridden ? (
            <div className='flex flex-wrap items-center gap-2'>
              <span>{row.effective_destination_label}</span>
              {unavailable ? (
                <Badge variant='outline' className='whitespace-nowrap'>
                  Saved choice unavailable
                </Badge>
              ) : (
                <Badge variant='secondary' className='whitespace-nowrap'>
                  Custom
                </Badge>
              )}
            </div>
          ) : (
            <div className='text-muted-foreground'>
              <span>{row.default_destination_label}</span>
              <span className='ml-2 text-xs whitespace-nowrap'>
                Roger default
              </span>
            </div>
          )}
        </TableCell>
        <TableCell>
          <div className='flex flex-wrap justify-end gap-2'>
            {!row.retired && !editing && (
              <Button
                variant='outline'
                size='sm'
                onClick={onEdit}
                disabled={busy}
                aria-label={`Edit ${row.fax_type_name}`}
              >
                <Pencil className='h-4 w-4' />
                Edit
              </Button>
            )}
            {overridden && (
              <Button
                variant='ghost'
                size='sm'
                onClick={onReset}
                disabled={busy}
                aria-label={`Reset ${row.fax_type_name} to default`}
              >
                <RotateCcw className='h-4 w-4' />
                Reset to default
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {editing && (
        <TableRow>
          <TableCell colSpan={3}>
            <RouteEditor
              row={row}
              miscTaskTypes={miscTaskTypes}
              busy={busy}
              onCancel={onCancel}
              onSave={onSave}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface RouteEditorProps {
  row: FaxRouteSettingRow;
  miscTaskTypes: MiscTaskTypeId[];
  busy: boolean;
  onCancel: () => void;
  onSave: (miscTaskType: MiscTaskTypeId, taskName: string) => void;
}

function RouteEditor({
  row,
  miscTaskTypes,
  busy,
  onCancel,
  onSave,
}: RouteEditorProps) {
  const current = row.override?.misc_task_type ?? row.default_misc_task_type;
  // A stored type this build does not offer opens empty, so Save waits for one.
  const known = (value: string) => offeredType(miscTaskTypes, value);
  const [taskType, setTaskType] = useState<MiscTaskTypeId | undefined>(
    known(current)
  );
  const [taskName, setTaskName] = useState(row.override?.task_name ?? '');
  // Save only a change from what the row shows now.
  const unchanged =
    taskType === current && taskName.trim() === (row.override?.task_name ?? '');
  const typeId = `route-type-${row.fax_type_id}`;
  const nameId = `route-name-${row.fax_type_id}`;

  return (
    <div className='space-y-3'>
      <div className='grid gap-3 sm:grid-cols-2'>
        <div className='space-y-1.5'>
          <Label htmlFor={typeId}>Task type</Label>
          <Select
            value={taskType ?? ''}
            onValueChange={value => setTaskType(known(value))}
            disabled={busy}
          >
            <SelectTrigger id={typeId} aria-label='Task type'>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              {miscTaskTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor={nameId}>Task name (optional)</Label>
          <Input
            id={nameId}
            maxLength={120}
            value={taskName}
            disabled={busy}
            onChange={event => setTaskName(event.target.value)}
          />
        </div>
      </div>
      {row.default_order_label && (
        <p className='text-sm text-muted-foreground'>
          {`The episode's ${row.default_order_label} order is still used first.`}
        </p>
      )}
      <div className='flex flex-wrap gap-2'>
        <Button
          size='sm'
          onClick={() => taskType && onSave(taskType, taskName)}
          disabled={busy || !taskType || unchanged}
        >
          Save
        </Button>
        <Button variant='outline' size='sm' onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
